import { Quote } from '@interfaces'
import { Product, ProductVariant } from '@interfaces/product.interface'
import { ecommerce, event } from 'vue-gtag'

type GtagItem = NonNullable<Parameters<typeof ecommerce>[1]['items']>[number]

export interface ItemList {
    id: string
    name: string
    index?: number
}

const productToGoogleProduct = (product: Product, itemList?: Partial<ItemList>) => {
    const item: GtagItem = {
        item_id: String(product.id),
        item_name: product.title,
        price: product.price,
    }

    if (product.brand) {
        item.item_brand = product.brand.name
    }

    const categories = product.categories?.slice(0, 5) || []

    for (let i = 0; i < categories.length; i++) {
        const key = i === 0 ? 'item_category' : `item_category${i + 1}`

        item[key] = categories[i].name
    }

    if (itemList) {
        item.item_list_id = itemList?.id
        item.item_list_name = itemList?.name
        item.index = itemList?.index
    }

    return item
}

const productVariantToGoogleProduct = (productVariant: ProductVariant, itemList?: Partial<ItemList>) => {
    const item: GtagItem = {
        item_id: String(productVariant.product!.id),
        item_name: productVariant.product!.title,
        price: productVariant.final_price,
    }

    if (productVariant.product?.brand) {
        item.item_brand = productVariant.product.brand.name
    }

    const categories = productVariant.product?.categories?.slice(0, 5) || []

    for (let i = 0; i < categories.length; i++) {
        const key = i === 0 ? 'item_category' : `item_category${i + 1}`

        item[key] = categories[i].name
    }

    if (itemList) {
        item.item_list_id = itemList?.id
        item.item_list_name = itemList?.name
        item.index = itemList?.index
    }

    return item
}

export function useTracking() {
    function trackProductsListView(
        products: Product[],
        itemList?: ItemList,
        pagination?: { page: number; perPage: number },
    ) {
        ecommerce('view_item_list', {
            items: products.map((product, index) => {
                const paginationIndex = (pagination?.page && pagination.perPage) ? (pagination.page - 1) * pagination.perPage : 0

                return productToGoogleProduct(product, {
                    ...itemList,
                    index: paginationIndex + index,
                })
            }),
            currency: 'EUR',
        })
    }

    function trackProductVariantsListView(
        productVariants: ProductVariant[],
        itemList?: ItemList,
        pagination?: { page: number; perPage: number },
    ) {
        ecommerce('view_item_list', {
            items: productVariants.map((variant, index) => {
                const paginationIndex = (pagination?.page && pagination.perPage) ? (pagination.page - 1) * pagination.perPage : 0

                return productVariantToGoogleProduct(variant, {
                    ...itemList,
                    index: paginationIndex + index,
                })
            }),
            currency: 'EUR',
        })
    }

    function trackPurchase(quote: Quote) {
        ecommerce('purchase', {
            transaction_id: quote.reference,
            items: quote.items!.filter((item) => item.type === 'product').map((item): GtagItem => ({
                item_id: String(item.product_variant!.id),
                item_name: item.name,
                quantity: item.quantity,
                price: item.price,
            })),
            value: quote.subtotal - quote.discount,
            tax: quote.tax,
            shipping: quote.shipping,
            currency: quote.currency,
        })

        event('conversion', {
            send_to: 'AW-11108395809',
            transaction_id: quote.reference,
            currency: quote.currency,
            value: quote.subtotal - quote.discount,
        })
    }

    function trackAddToCart(product: Product, quantity: number, itemList?: ItemList) {
        ecommerce('add_to_cart', {
            items: [{
                ...productToGoogleProduct(product, itemList),
                quantity,
            }],
            currency: 'EUR',
            value: product.price * quantity,
        })
    };

    const trackAddToWishlist = (productVariant: ProductVariant, itemList?: ItemList) => {
        ecommerce('add_to_wishlist', {
            items: [
                productVariantToGoogleProduct(productVariant, itemList),
            ],
            currency: 'EUR',
            value: productVariant.final_price,
        })
    }

    return {
        trackProductsListView,
        trackProductVariantsListView,
        trackPurchase,
        trackAddToCart,
        trackAddToWishlist,
    }
}
